import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Collapse,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Plus,
  Eye,
  Edit,
  Trash2,
  ArrowUpDown,
  Filter,
  Search,
  X,
  Calendar,
  FileText,
  Check
} from 'lucide-react';
import { motion } from 'framer-motion';
import AccountingService, { AccountingTransaction } from '../services/accountingService';
import { unitApi, Unit } from '../services/api';
import { getApiUrl } from '../config/api';
import HierarchicalUnitSelector from './HierarchicalUnitSelector';

interface Property {
  id: number;
  name: string;
  currency_id?: number;
  currency?: Currency;
}

interface Currency {
  id: number;
  name: string;
  symbol: string;
  code: string;
}

interface TransactionFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  transactionType?: string;
  status?: string;
  search?: string;
  propertyId?: number | null;
  unitId?: number | null;
}

interface AccountingTransactionsProps {
  properties: Property[];
  currencies: Currency[];
  selectedPropertyId?: number;
  onPropertyChange: (propertyId?: number) => void;
  selectedUnitId?: number;
  onUnitChange?: (unitId?: number) => void;
}

const AccountingTransactions: React.FC<AccountingTransactionsProps> = ({
  properties,
  currencies,
  selectedPropertyId,
  onPropertyChange,
  selectedUnitId,
  onUnitChange
}) => {
  const [transactions, setTransactions] = useState<AccountingTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<AccountingTransaction | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTransactionId, setDeleteTransactionId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [committingTransaction, setCommittingTransaction] = useState<number | null>(null);
  
  // New Transaction Dialog states
  const [showNewTransactionDialog, setShowNewTransactionDialog] = useState(false);
  const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [newTransactionForm, setNewTransactionForm] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    transaction_type: '',
    property_id: selectedPropertyId || '',
    unit_id: selectedUnitId || '',
    unit_number: '',
    unit_name: '',
    currency_id: '',
    journal_entries: [
      { account_id: '', debit_amount: '', credit_amount: '', description: '' },
      { account_id: '', debit_amount: '', credit_amount: '', description: '' }
    ],
    auto_post: false
  });
  const [submittingTransaction, setSubmittingTransaction] = useState(false);
  
  // Edit Transaction Dialog states
  const [showEditTransactionDialog, setShowEditTransactionDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<AccountingTransaction | null>(null);
  const [editTransactionForm, setEditTransactionForm] = useState({
    transaction_date: '',
    description: '',
    transaction_type: '',
    property_id: '',
    unit_id: '',
    unit_number: '',
    unit_name: '',
    currency_id: '',
    journal_entries: [
      { account_id: '', debit_amount: '', credit_amount: '', description: '' }
    ],
    auto_post: false
  });
  const [updatingTransaction, setUpdatingTransaction] = useState(false);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [filters, setFilters] = useState<TransactionFilters>({
    startDate: null,
    endDate: null,
    transactionType: '',
    status: '',
    search: '',
    propertyId: selectedPropertyId || null,
    unitId: selectedUnitId || null
  });

  // Pagination states
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [allTransactions, setAllTransactions] = useState<AccountingTransaction[]>([]);

  // Get paginated transactions
  const paginatedTransactions = React.useMemo(() => {
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return allTransactions.slice(startIndex, endIndex);
  }, [allTransactions, currentPage, perPage]);

  const totalPages = Math.ceil(allTransactions.length / perPage);

  // Helper function to get property currency ID for smart currency selection
  const getPropertyCurrencyId = (propertyId?: string | number) => {
    if (!propertyId) return getDefaultCurrencyId();
    
    const selectedProperty = properties.find(p => p.id === parseInt(propertyId.toString()));
    
    // First try to get from property.currency relationship
    if (selectedProperty?.currency?.id) {
      return selectedProperty.currency.id.toString();
    }
    
    // If property has currency_id, use it directly
    if (selectedProperty?.currency_id) {
      return selectedProperty.currency_id.toString();
    }
    
    // Fallback to default currency
    return getDefaultCurrencyId();
  };

  // Helper function to get default currency ID
  const getDefaultCurrencyId = () => {
    if (Array.isArray(currencies)) {
      const defaultCurrency = currencies.find((c: any) => c.is_default);
      if (defaultCurrency?.id) {
        return defaultCurrency.id.toString();
      }
      
      // If no default currency, use first available currency
      if (currencies.length > 0) {
        return currencies[0].id.toString();
      }
    }
    
    // Ultimate fallback
    return '';
  };

  // Load transactions
  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        property_id: filters.propertyId || selectedPropertyId,
        unit_id: filters.unitId || selectedUnitId,
        per_page: 1000 // Load more transactions for client-side pagination
      };

      // Add date filters
      if (filters.startDate) {
        params.start_date = filters.startDate.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        params.end_date = filters.endDate.toISOString().split('T')[0];
      }

      // Add other filters
      if (filters.status) {
        params.status = filters.status;
      }

      const response = await AccountingService.getTransactions(params);
      
      let transactionsData = Array.isArray(response.data) ? response.data : [];

      // Apply client-side filters
      if (filters.transactionType) {
        transactionsData = transactionsData.filter(t => t.transaction_type === filters.transactionType);
      }

      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        transactionsData = transactionsData.filter(t => 
          t.description.toLowerCase().includes(searchTerm) ||
          t.reference_number.toLowerCase().includes(searchTerm) ||
          (t.property?.name && t.property.name.toLowerCase().includes(searchTerm)) ||
          (t.unit_number && t.unit_number.toLowerCase().includes(searchTerm))
        );
      }

      setAllTransactions(transactionsData);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (error: any) {
      console.error('Error loading transactions:', error);
      setAllTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId, selectedUnitId, filters]);

  // Load all units for hierarchical selector
  const loadUnits = useCallback(async () => {
    try {
      setLoadingUnits(true);
      const units = await unitApi.getAll();
      setUnits(units || []);
    } catch (error) {
      console.error('Error loading units:', error);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  }, []);

  // Load chart of accounts for the selected property
  const loadChartOfAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const accounts = await AccountingService.getChartOfAccounts(selectedPropertyId);
      setChartOfAccounts(accounts || []);
    } catch (error) {
      console.error('Error loading chart of accounts:', error);
      setChartOfAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [selectedPropertyId]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      transactionType: '',
      status: '',
      search: '',
      propertyId: selectedPropertyId || null,
      unitId: selectedUnitId || null
    });
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof TransactionFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    // If property changes, reset unit (units are already loaded)
    if (key === 'propertyId') {
      setFilters(prev => ({ ...prev, propertyId: value, unitId: null }));
    }
  };

  // Helper function to get hierarchical unit name by unit ID
  const getUnitHierarchyNameById = (unitId: number): string => {
    const unit = units?.find((u: any) => u.id === unitId);
    if (!unit) return 'Unknown Unit';
    
    // Build parent path using units array
    const buildParentPath = (currentUnit: any): string => {
      const parent = units?.find((u: any) => u.id === currentUnit.parent_unit_id);
      if (!parent) return currentUnit.unit_number;
      
      const parentPath = buildParentPath(parent);
      return `${parentPath} → ${currentUnit.unit_number}`;
    };
    
    return buildParentPath(unit);
  };

  // Check if any filters are active
  const hasActiveFilters = filters.startDate || filters.endDate || filters.transactionType || filters.status || filters.search || 
    (filters.propertyId && filters.propertyId !== selectedPropertyId) || 
    (filters.unitId && filters.unitId !== selectedUnitId);

  // Delete transaction handlers
  const handleDeleteTransaction = (transactionId: number) => {
    setDeleteTransactionId(transactionId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTransaction = async () => {
    try {
      if (!deleteTransactionId) return;
      await AccountingService.deleteTransaction(deleteTransactionId);
      await loadTransactions();
      setShowDeleteConfirm(false);
      setDeleteTransactionId(null);
      setSnackbar({ open: true, message: 'Transaction deleted successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete transaction';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // New Transaction handlers
  const handleOpenNewTransactionDialog = () => {
    // Reset form with current selected property and its currency (or fallback)
    const smartCurrencyId = getPropertyCurrencyId(selectedPropertyId);
    setNewTransactionForm({
      transaction_date: new Date().toISOString().split('T')[0],
      description: '',
      transaction_type: '',
      property_id: selectedPropertyId || '',
      unit_id: selectedUnitId || '',
      unit_number: '',
      unit_name: '',
      currency_id: smartCurrencyId,
      journal_entries: [
        { account_id: '', debit_amount: '', credit_amount: '', description: '' },
        { account_id: '', debit_amount: '', credit_amount: '', description: '' }
      ],
      auto_post: false
    });
    setShowNewTransactionDialog(true);
  };

  const handleCloseNewTransactionDialog = () => {
    setShowNewTransactionDialog(false);
  };

  const handleNewTransactionFormChange = (field: string, value: any) => {
    setNewTransactionForm(prev => {
      const updatedForm = {
        ...prev,
        [field]: value
      };
      
      // Auto-select currency when property changes using smart selection
      if (field === 'property_id' && value) {
        updatedForm.currency_id = getPropertyCurrencyId(value);
      }
      
      return updatedForm;
    });
  };

  const handleJournalEntryChange = (index: number, field: string, value: any) => {
    setNewTransactionForm(prev => ({
      ...prev,
      journal_entries: prev.journal_entries.map((entry, i) => 
        i === index ? { ...entry, [field]: value } : entry
      )
    }));
  };

  const addJournalEntry = () => {
    setNewTransactionForm(prev => ({
      ...prev,
      journal_entries: [
        ...prev.journal_entries,
        { account_id: '', debit_amount: '', credit_amount: '', description: '' }
      ]
    }));
  };

  const removeJournalEntry = (index: number) => {
    if (newTransactionForm.journal_entries.length > 2) {
      setNewTransactionForm(prev => ({
        ...prev,
        journal_entries: prev.journal_entries.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const totalDebits = newTransactionForm.journal_entries.reduce((sum, entry) => 
      sum + (parseFloat(entry.debit_amount) || 0), 0
    );
    const totalCredits = newTransactionForm.journal_entries.reduce((sum, entry) => 
      sum + (parseFloat(entry.credit_amount) || 0), 0
    );
    return { totalDebits, totalCredits, isBalanced: Math.abs(totalDebits - totalCredits) < 0.01 };
  };

  const handleSubmitNewTransaction = async () => {
    try {
      setSubmittingTransaction(true);
      
      // Validate form
      if (!newTransactionForm.description || !newTransactionForm.transaction_type || !newTransactionForm.property_id || !newTransactionForm.currency_id) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      // Validate journal entries
      const validEntries = newTransactionForm.journal_entries.filter(entry => 
        entry.account_id && entry.description && (entry.debit_amount || entry.credit_amount)
      );

      if (validEntries.length < 2) {
        setSnackbar({ open: true, message: 'At least 2 journal entries are required', severity: 'error' });
        return;
      }

      const { isBalanced } = calculateTotals();
      if (!isBalanced) {
        setSnackbar({ open: true, message: 'Debits must equal credits', severity: 'error' });
        return;
      }

      // Prepare data for API
      const transactionData = {
        transaction_date: newTransactionForm.transaction_date,
        description: newTransactionForm.description,
        transaction_type: newTransactionForm.transaction_type,
        property_id: parseInt(newTransactionForm.property_id.toString()),
        unit_id: newTransactionForm.unit_id ? parseInt(newTransactionForm.unit_id.toString()) : undefined,
        unit_number: newTransactionForm.unit_number || undefined,
        unit_name: newTransactionForm.unit_name || undefined,
        currency_id: parseInt(newTransactionForm.currency_id.toString()),
        journal_entries: validEntries.map(entry => ({
          account_id: parseInt(entry.account_id.toString()),
          debit_amount: parseFloat(entry.debit_amount) || 0,
          credit_amount: parseFloat(entry.credit_amount) || 0,
          description: entry.description
        })),
        auto_post: newTransactionForm.auto_post
      };

      await AccountingService.createTransaction(transactionData);
      setSnackbar({ open: true, message: 'Transaction created successfully!', severity: 'success' });
      setShowNewTransactionDialog(false);
      await loadTransactions();
    } catch (error: any) {
      console.error('Error creating transaction:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create transaction';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSubmittingTransaction(false);
    }
  };

  // Edit Transaction handlers
  const handleOpenEditTransactionDialog = (transaction: AccountingTransaction) => {
    setEditingTransaction(transaction);
    
    // Populate edit form with existing transaction data
    setEditTransactionForm({
      transaction_date: transaction.transaction_date,
      description: transaction.description,
      transaction_type: transaction.transaction_type,
      property_id: transaction.property_id?.toString() || '',
      unit_id: transaction.unit_id?.toString() || '',
      unit_number: transaction.unit_number || '',
      unit_name: transaction.unit_name || '',
      currency_id: transaction.currency_id?.toString() || '',
      journal_entries: transaction.journal_entries?.map(entry => ({
        account_id: entry.account_id.toString(),
        debit_amount: entry.debit_amount?.toString() || '',
        credit_amount: entry.credit_amount?.toString() || '',
        description: entry.description || ''
      })) || [
        { account_id: '', debit_amount: '', credit_amount: '', description: '' },
        { account_id: '', debit_amount: '', credit_amount: '', description: '' }
      ],
      auto_post: false
    });
    
    setShowEditTransactionDialog(true);
  };

  const handleCloseEditTransactionDialog = () => {
    setShowEditTransactionDialog(false);
    setEditingTransaction(null);
  };

  const handleEditTransactionFormChange = (field: string, value: any) => {
    setEditTransactionForm(prev => {
      const updatedForm = {
        ...prev,
        [field]: value
      };
      
      // Auto-select currency when property changes using smart selection
      if (field === 'property_id' && value) {
        updatedForm.currency_id = getPropertyCurrencyId(value);
      }
      
      return updatedForm;
    });
  };

  const handleEditJournalEntryChange = (index: number, field: string, value: any) => {
    setEditTransactionForm(prev => ({
      ...prev,
      journal_entries: prev.journal_entries.map((entry, i) => 
        i === index ? { ...entry, [field]: value } : entry
      )
    }));
  };

  const addEditJournalEntry = () => {
    setEditTransactionForm(prev => ({
      ...prev,
      journal_entries: [
        ...prev.journal_entries,
        { account_id: '', debit_amount: '', credit_amount: '', description: '' }
      ]
    }));
  };

  const removeEditJournalEntry = (index: number) => {
    if (editTransactionForm.journal_entries.length > 2) {
      setEditTransactionForm(prev => ({
        ...prev,
        journal_entries: prev.journal_entries.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateEditTotals = () => {
    const totalDebits = editTransactionForm.journal_entries.reduce((sum, entry) => 
      sum + (parseFloat(entry.debit_amount) || 0), 0
    );
    const totalCredits = editTransactionForm.journal_entries.reduce((sum, entry) => 
      sum + (parseFloat(entry.credit_amount) || 0), 0
    );
    return { totalDebits, totalCredits, isBalanced: Math.abs(totalDebits - totalCredits) < 0.01 };
  };

  const handleSubmitEditTransaction = async () => {
    if (!editingTransaction) return;
    
    try {
      setUpdatingTransaction(true);
      
      // Validate form
      if (!editTransactionForm.description || !editTransactionForm.transaction_type || !editTransactionForm.property_id || !editTransactionForm.currency_id) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      // Validate journal entries
      const validEntries = editTransactionForm.journal_entries.filter(entry => 
        entry.account_id && entry.description && (entry.debit_amount || entry.credit_amount)
      );

      if (validEntries.length < 2) {
        setSnackbar({ open: true, message: 'At least 2 journal entries are required', severity: 'error' });
        return;
      }

      const { isBalanced } = calculateEditTotals();
      if (!isBalanced) {
        setSnackbar({ open: true, message: 'Debits must equal credits', severity: 'error' });
        return;
      }

      // Prepare data for API
      const transactionData = {
        transaction_date: editTransactionForm.transaction_date,
        description: editTransactionForm.description,
        transaction_type: editTransactionForm.transaction_type,
        property_id: parseInt(editTransactionForm.property_id.toString()),
        unit_id: editTransactionForm.unit_id ? parseInt(editTransactionForm.unit_id.toString()) : undefined,
        unit_number: editTransactionForm.unit_number || undefined,
        unit_name: editTransactionForm.unit_name || undefined,
        currency_id: parseInt(editTransactionForm.currency_id.toString()),
        journal_entries: validEntries.map(entry => ({
          account_id: parseInt(entry.account_id.toString()),
          debit_amount: parseFloat(entry.debit_amount) || 0,
          credit_amount: parseFloat(entry.credit_amount) || 0,
          description: entry.description
        })),
        auto_post: editTransactionForm.auto_post
      };

      await AccountingService.updateTransaction(editingTransaction.id, transactionData);
      setSnackbar({ open: true, message: 'Transaction updated successfully!', severity: 'success' });
      setShowEditTransactionDialog(false);
      await loadTransactions();
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update transaction';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setUpdatingTransaction(false);
    }
  };

  // Commit transaction handler
  const handleCommitTransaction = async (transactionId: number) => {
    try {
      setCommittingTransaction(transactionId);
      
      // Call API to commit/post the transaction
      const response = await fetch(getApiUrl(`/accounting/transactions/${transactionId}/post`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        let errorMessage = 'Failed to commit transaction';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('Transaction committed successfully:', result);
      
      await loadTransactions();
      
      // Dispatch event to refresh financial reports
      window.dispatchEvent(new CustomEvent('transactionCommitted'));
      
      setSnackbar({ 
        open: true, 
        message: 'Transaction committed successfully!', 
        severity: 'success' 
      });
    } catch (error: any) {
      console.error('Error committing transaction:', error);
      
      let errorMessage = 'Failed to commit transaction';
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check if the backend is running.';
      } else if (error.name === 'TypeError' && error.message.includes('CONNECTION_REFUSED')) {
        errorMessage = 'Connection refused. Please check if the backend server is running on the correct port.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setCommittingTransaction(null);
    }
  };

  // Effects
  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Load all units once when component mounts
  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  // Load chart of accounts when dialog opens
  useEffect(() => {
    if (showNewTransactionDialog) {
      loadChartOfAccounts();
    }
  }, [showNewTransactionDialog, selectedPropertyId]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      loadTransactions();
    };

    window.addEventListener('transactionCreated', handleRefresh);
    return () => window.removeEventListener('transactionCreated', handleRefresh);
  }, [loadTransactions]);

  // Transaction List Item Component
  const TransactionListItem = ({ transaction }: { transaction: AccountingTransaction }) => {
    return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
    >
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{
                width: 56,
                height: 56,
                background: transaction.transaction_type === 'BOOKING_REVENUE' || transaction.transaction_type === 'PAYMENT_RECEIVED'
                  ? 'linear-gradient(135deg, #4caf50, #2e7d32)'
                  : 'linear-gradient(135deg, #f44336, #c62828)',
                borderRadius: 3
              }}>
                {transaction.transaction_type === 'BOOKING_REVENUE' || transaction.transaction_type === 'PAYMENT_RECEIVED' ? (
                  <ArrowUpDown size={28} style={{ transform: 'rotate(180deg)' }} />
                ) : (
                  <ArrowUpDown size={28} />
                )}
              </Avatar>
              
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {transaction.reference_number}
                  </Typography>
                  <Chip
                    label={transaction.status}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      ...(transaction.status === 'POSTED' && { bgcolor: '#e8f5e8', color: '#2e7d32' }),
                      ...(transaction.status === 'DRAFT' && { bgcolor: '#fff3e0', color: '#f57c00' })
                    }}
                  />
                </Box>
                
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  {transaction.description}
                </Typography>
                
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {transaction.property?.name}
                  {transaction.unit_id && ` - ${getUnitHierarchyNameById(transaction.unit_id)}`}
                  {!transaction.unit_id && transaction.unit_number && ` - ${transaction.unit_number}`}
                  {!transaction.unit_id && transaction.unit_name && ` - ${transaction.unit_name}`}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold',
                color: transaction.transaction_type === 'BOOKING_REVENUE' || transaction.transaction_type === 'PAYMENT_RECEIVED'
                  ? '#2e7d32' : '#c62828',
                minWidth: 120,
                textAlign: 'right'
              }}>
                {transaction.transaction_type === 'BOOKING_REVENUE' || transaction.transaction_type === 'PAYMENT_RECEIVED' ? '+' : '-'}
                {transaction.currency?.symbol || '$'}{Number(transaction.total_amount).toLocaleString()}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {/* Commit Action for Draft Transactions */}
                {transaction.status === 'DRAFT' && (
                  <IconButton 
                    size="small"
                    onClick={() => handleCommitTransaction(transaction.id)}
                    disabled={committingTransaction === transaction.id}
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { color: 'warning.main', bgcolor: 'warning.50' },
                      '&:disabled': { color: 'text.disabled' }
                    }}
                    title="Commit Transaction"
                  >
                    {committingTransaction === transaction.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Check size={20} />
                    )}
                  </IconButton>
                )}
                
                <IconButton 
                  size="small"
                  onClick={() => setSelectedTransaction(transaction)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}
                  title="View Details"
                >
                  <Eye size={20} />
                </IconButton>
                <IconButton 
                  size="small"
                  onClick={() => handleOpenEditTransactionDialog(transaction)}
                  disabled={transaction.status === 'POSTED'}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { color: 'success.main', bgcolor: 'success.50' },
                    '&:disabled': { color: 'text.disabled' }
                  }}
                  title="Edit Transaction"
                >
                  <Edit size={20} />
                </IconButton>
                <IconButton 
                  size="small"
                  onClick={() => handleDeleteTransaction(transaction.id)}
                  sx={{ 
                    color: 'text.secondary',
                    cursor: 'pointer',
                    '&:hover': { color: 'error.main', bgcolor: 'error.50' }
                  }}
                  title="Delete Transaction"
                >
                  <Trash2 size={20} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Transactions
            {allTransactions.length > 0 && (
              <Chip
                label={`${allTransactions.length} transaction${allTransactions.length !== 1 ? 's' : ''}`}
                size="medium"
                sx={{ 
                  ml: 2, 
                  bgcolor: selectedPropertyId ? 'primary.100' : 'success.100',
                  color: selectedPropertyId ? 'primary.700' : 'success.700',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
            {selectedPropertyId 
              ? `Manage transactions for ${properties.find(p => p.id === selectedPropertyId)?.name || 'selected property'}`
              : 'Viewing transactions across all properties'
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Per Page Selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={perPage}
              label="Per Page"
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.300',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                }
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={250}>250</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={showFilters ? "contained" : "outlined"}
            startIcon={<Filter size={20} />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              ...(hasActiveFilters && !showFilters && {
                bgcolor: 'warning.100',
                borderColor: 'warning.500',
                color: 'warning.700',
                '&:hover': {
                  bgcolor: 'warning.200',
                  borderColor: 'warning.600',
                }
              })
            }}
          >
            Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v && v !== '').length})`}
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleOpenNewTransactionDialog}
            sx={{
              background: 'linear-gradient(135deg, #1976d2, #9c27b0)',
              borderRadius: 3,
              px: 3,
              py: 1.5
            }}
          >
            New Transaction
          </Button>
        </Box>
      </Box>

      {/* Filter Panel */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 4, borderRadius: 3, border: '2px solid', borderColor: 'primary.100' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Filter Transactions
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {hasActiveFilters && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<X size={16} />}
                    onClick={clearFilters}
                    sx={{ borderRadius: 2 }}
                  >
                    Clear All
                  </Button>
                )}
                <IconButton 
                  size="small" 
                  onClick={() => setShowFilters(false)}
                  sx={{ color: 'text.secondary' }}
                >
                  <X size={20} />
                </IconButton>
              </Box>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={3}>
                {/* Property Selector */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={filters.propertyId || ''}
                      onChange={(e) => handleFilterChange('propertyId', e.target.value || null)}
                      label="Property"
                    >
                      <MenuItem value="">All Properties</MenuItem>
                      {properties.map((property) => (
                        <MenuItem key={property.id} value={property.id}>
                          {property.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Unit Selector */}
                <Grid item xs={12} md={6}>
                  <HierarchicalUnitSelector
                    units={units}
                    selectedPropertyId={filters.propertyId || selectedPropertyId || ''}
                    value={filters.unitId?.toString() || ''}
                    onChange={(value) => handleFilterChange('unitId', value ? parseInt(value) : null)}
                    label="Unit"
                    showAllOption={true}
                    disabled={!filters.propertyId && !selectedPropertyId}
                  />
                </Grid>

                {/* Search */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Search by description, reference, property..."
                    value={filters.search || ''}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    InputProps={{
                      startAdornment: <Search size={20} style={{ marginRight: 8, color: '#666' }} />
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>

                {/* Transaction Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Transaction Type</InputLabel>
                    <Select
                      value={filters.transactionType || ''}
                      onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                      label="Transaction Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="BOOKING_REVENUE">Booking Revenue</MenuItem>
                      <MenuItem value="EXPENSE">Expense</MenuItem>
                      <MenuItem value="PAYMENT_RECEIVED">Payment Received</MenuItem>
                      <MenuItem value="PAYMENT_MADE">Payment Made</MenuItem>
                      <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                      <MenuItem value="DEPRECIATION">Depreciation</MenuItem>
                      <MenuItem value="ACCRUAL">Accrual</MenuItem>
                      <MenuItem value="REVERSAL">Reversal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status || ''}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="POSTED">Posted</MenuItem>
                      <MenuItem value="REVERSED">Reversed</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Start Date */}
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(date) => handleFilterChange('startDate', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          startAdornment: <Calendar size={20} style={{ marginRight: 8, color: '#666' }} />
                        }
                      }
                    }}
                  />
                </Grid>

                {/* End Date */}
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={filters.endDate}
                    onChange={(date) => handleFilterChange('endDate', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          startAdornment: <Calendar size={20} style={{ marginRight: 8, color: '#666' }} />
                        }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Active Filters:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {filters.propertyId && filters.propertyId !== selectedPropertyId && (
                      <Chip
                        label={`Property: ${properties.find(p => p.id === filters.propertyId)?.name || 'Unknown'}`}
                        size="small"
                        onDelete={() => handleFilterChange('propertyId', null)}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                    {filters.unitId && filters.unitId !== selectedUnitId && (
                      <Chip
                        label={`Unit: ${getUnitHierarchyNameById(filters.unitId)}`}
                        size="small"
                        onDelete={() => handleFilterChange('unitId', null)}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                    {filters.search && (
                      <Chip
                        label={`Search: "${filters.search}"`}
                        size="small"
                        onDelete={() => handleFilterChange('search', '')}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                    {filters.transactionType && (
                      <Chip
                        label={`Type: ${filters.transactionType}`}
                        size="small"
                        onDelete={() => handleFilterChange('transactionType', '')}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                    {filters.status && (
                      <Chip
                        label={`Status: ${filters.status}`}
                        size="small"
                        onDelete={() => handleFilterChange('status', '')}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                    {filters.startDate && (
                      <Chip
                        label={`From: ${filters.startDate.toLocaleDateString()}`}
                        size="small"
                        onDelete={() => handleFilterChange('startDate', null)}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                    {filters.endDate && (
                      <Chip
                        label={`To: ${filters.endDate.toLocaleDateString()}`}
                        size="small"
                        onDelete={() => handleFilterChange('endDate', null)}
                        sx={{ bgcolor: 'primary.50', color: 'primary.700' }}
                      />
                    )}
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Collapse>

      {/* Loading State */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Loading transactions...
          </Typography>
        </Box>
      )}

      {/* Transactions List */}
      {!loading && (
        <Box>
          {/* Results Summary */}
          {hasActiveFilters && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <FileText size={16} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                Showing {paginatedTransactions.length} of {allTransactions.length} transaction{allTransactions.length !== 1 ? 's' : ''} 
                {totalPages > 1 && ` (page ${currentPage} of ${totalPages})`}
                {filters.startDate || filters.endDate ? ' for the selected date range' : ''}
                {filters.transactionType ? ` of type "${filters.transactionType}"` : ''}
                {filters.status ? ` with status "${filters.status}"` : ''}
              </Typography>
            </Box>
          )}

          {paginatedTransactions.map((transaction) => (
            <TransactionListItem key={transaction.id} transaction={transaction} />
          ))}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                sx={{ borderRadius: 2 }}
              >
                Previous
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "contained" : "outlined"}
                      onClick={() => setCurrentPage(pageNum)}
                      size="small"
                      sx={{ 
                        minWidth: 40,
                        borderRadius: 2,
                        ...(currentPage === pageNum && {
                          bgcolor: 'primary.main',
                          color: 'white'
                        })
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </Box>
              <Button
                variant="outlined"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                sx={{ borderRadius: 2 }}
              >
                Next
              </Button>
            </Box>
          )}
          
          {/* Transactions Footer - Summary */}
          {allTransactions.length > 0 && (
            <Card sx={{ mt: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Transaction Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(() => {
                      // Group current page transactions by currency
                      const currentPageCurrencyTotals = paginatedTransactions.reduce((acc, transaction) => {
                        const currency = transaction.currency?.symbol || '$';
                        if (!acc[currency]) {
                          acc[currency] = 0;
                        }
                        acc[currency] += Number(transaction.total_amount) || 0;
                        return acc;
                      }, {} as Record<string, number>);

                      // Group cumulative transactions (from page 1 to current page) by currency
                      const endIndex = currentPage * perPage;
                      const cumulativeTransactions = allTransactions.slice(0, endIndex);
                      const cumulativeCurrencyTotals = cumulativeTransactions.reduce((acc, transaction) => {
                        const currency = transaction.currency?.symbol || '$';
                        if (!acc[currency]) {
                          acc[currency] = 0;
                        }
                        acc[currency] += Number(transaction.total_amount) || 0;
                        return acc;
                      }, {} as Record<string, number>);

                      const currentPageEntries = Object.entries(currentPageCurrencyTotals);
                      const cumulativeEntries = Object.entries(cumulativeCurrencyTotals);
                      const isLastPage = currentPage === totalPages;

                      return (
                        <>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                              Current Page Amount
                            </Typography>
                            {currentPageEntries.length === 0 ? (
                              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                $0
                              </Typography>
                            ) : currentPageEntries.length === 1 ? (
                              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                {currentPageEntries[0][0]}{currentPageEntries[0][1].toLocaleString()}
                              </Typography>
                            ) : (
                              <Box>
                                {currentPageEntries.map(([currency, amount]) => (
                                  <Typography key={currency} variant="body1" sx={{ color: 'primary.main', fontWeight: 500 }}>
                                    {currency}{amount.toLocaleString()}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                              {isLastPage ? 'Total Amount (All Pages)' : `Total Amount (Pages 1-${currentPage})`}
                            </Typography>
                            {cumulativeEntries.length === 0 ? (
                              <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                $0
                              </Typography>
                            ) : cumulativeEntries.length === 1 ? (
                              <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {cumulativeEntries[0][0]}{cumulativeEntries[0][1].toLocaleString()}
                              </Typography>
                            ) : (
                              <Box>
                                {cumulativeEntries.map(([currency, amount]) => (
                                  <Typography key={currency} variant="body1" sx={{ color: 'success.main', fontWeight: 500 }}>
                                    {currency}{amount.toLocaleString()}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right', borderLeft: '1px solid', borderColor: 'grey.300', pl: 3 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                              Transaction Count
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                              {isLastPage ? allTransactions.length : cumulativeTransactions.length}
                            </Typography>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Box>
                
                {/* Additional info if filters are applied */}
                {hasActiveFilters && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      💡 Note: Amounts shown are for the current filtered results. 
                      Total amount shows cumulative sum from page 1 to current page{currentPage === totalPages ? ' (all pages)' : ''}.
                      {filters.startDate || filters.endDate ? ' Date range filters are applied.' : ''}
                      {filters.transactionType ? ` Showing only "${filters.transactionType}" transactions.` : ''}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
          
          {allTransactions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                {hasActiveFilters ? 'No transactions match your filters' : 'No transactions found'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {hasActiveFilters 
                  ? 'Try adjusting your filter criteria to see more results.'
                  : selectedPropertyId 
                    ? 'No transactions for this property yet.' 
                    : 'No transactions found. Create your first transaction!'
                }
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this transaction? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={confirmDeleteTransaction} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Modal */}
      <Dialog
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedTransaction.reference_number}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Description:</strong> {selectedTransaction.description}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Date:</strong> {selectedTransaction.transaction_date}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Type:</strong> {selectedTransaction.transaction_type}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Property & Unit:</strong> {selectedTransaction.property?.name}
                {selectedTransaction.unit_id && ` - ${getUnitHierarchyNameById(selectedTransaction.unit_id)}`}
                {!selectedTransaction.unit_id && selectedTransaction.unit_number && ` - ${selectedTransaction.unit_number}`}
                {!selectedTransaction.unit_id && selectedTransaction.unit_name && ` - ${selectedTransaction.unit_name}`}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Total Amount:</strong> {selectedTransaction.currency?.symbol || '$'}{Number(selectedTransaction.total_amount).toLocaleString()}
              </Typography>

              {selectedTransaction.journal_entries && selectedTransaction.journal_entries.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Journal Entries</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Account</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Debit</TableCell>
                          <TableCell align="right">Credit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTransaction.journal_entries.map((entry, index) => (
                          <TableRow key={index}>
                            <TableCell>{entry.account?.name}</TableCell>
                            <TableCell>{entry.description}</TableCell>
                            <TableCell align="right">
                              {Number(entry.debit_amount) > 0 ? `${selectedTransaction.currency?.symbol || '$'}${Number(entry.debit_amount).toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {Number(entry.credit_amount) > 0 ? `${selectedTransaction.currency?.symbol || '$'}${Number(entry.credit_amount).toFixed(2)}` : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTransaction(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New Transaction Dialog */}
      <Dialog 
        open={showNewTransactionDialog} 
        onClose={handleCloseNewTransactionDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
            overflow: 'visible',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 2,
          position: 'relative'
        }}>
          <Typography variant="h6" component="div" sx={{ 
            fontWeight: '600',
            textAlign: 'center',
            letterSpacing: '0.3px'
          }}>
            Create New Transaction
          </Typography>
          <Box sx={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Plus size={16} color="white" />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }}>
            <Grid container spacing={3}>
              {/* Basic Transaction Information */}
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2.5, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                }}>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: '600', 
                    mb: 2,
                    color: '#2d3748',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1rem'
                  }}>
                    <FileText size={18} />
                    Transaction Details
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transaction Date"
                        type="date"
                        value={newTransactionForm.transaction_date}
                        onChange={(e) => handleNewTransactionFormChange('transaction_date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Transaction Type</InputLabel>
                        <Select
                          value={newTransactionForm.transaction_type}
                          label="Transaction Type"
                          onChange={(e) => handleNewTransactionFormChange('transaction_type', e.target.value)}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <MenuItem value="BOOKING_REVENUE">📊 Booking Revenue</MenuItem>
                          <MenuItem value="EXPENSE">💰 Expense</MenuItem>
                          <MenuItem value="PAYMENT_RECEIVED">💳 Payment Received</MenuItem>
                          <MenuItem value="PAYMENT_MADE">💸 Payment Made</MenuItem>
                          <MenuItem value="ADJUSTMENT">⚖️ Journal Adjustment</MenuItem>
                          <MenuItem value="DEPRECIATION">📉 Depreciation</MenuItem>
                          <MenuItem value="ACCRUAL">📈 Accrual</MenuItem>
                          <MenuItem value="REVERSAL">🔄 Reversal</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={newTransactionForm.description}
                        onChange={(e) => handleNewTransactionFormChange('description', e.target.value)}
                        multiline
                        rows={2}
                        required
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }
                        }}
                        placeholder="Enter transaction description..."
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Property</InputLabel>
                        <Select
                          value={newTransactionForm.property_id}
                          label="Property"
                          onChange={(e) => handleNewTransactionFormChange('property_id', e.target.value)}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          {properties.map((property) => (
                            <MenuItem key={property.id} value={property.id}>
                              🏢 {property.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Currency</InputLabel>
                        <Select
                          value={newTransactionForm.currency_id}
                          label="Currency"
                          onChange={(e) => handleNewTransactionFormChange('currency_id', e.target.value)}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          {currencies.map((currency) => (
                            <MenuItem key={currency.id} value={currency.id}>
                              {currency.symbol} {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit (Optional)</InputLabel>
                        <Select
                          value={newTransactionForm.unit_id}
                          label="Unit (Optional)"
                          onChange={(e) => handleNewTransactionFormChange('unit_id', e.target.value)}
                          disabled={loadingUnits}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <MenuItem value="">🏠 None</MenuItem>
                          {units.map((unit) => (
                            <MenuItem key={unit.id} value={unit.id}>
                              🏠 {unit.unit_number} - {unit.name || 'Unit'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Journal Entries */}
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2.5, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: '600',
                      color: '#2d3748',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}>
                      📋 Journal Entries
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Plus size={16} />}
                      onClick={addJournalEntry}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6b4190 100%)',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Add Entry
                    </Button>
                  </Box>
                  
                  <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                    {newTransactionForm.journal_entries.map((entry, index) => (
                      <Card key={index} sx={{ 
                        mb: 2, 
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          transform: 'translateY(-1px)'
                        }
                      }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#4a5568' }}>
                              Entry #{index + 1}
                            </Typography>
                            <IconButton
                              color="error"
                              onClick={() => removeJournalEntry(index)}
                              disabled={newTransactionForm.journal_entries.length <= 2}
                              size="small"
                              sx={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                '&:hover': {
                                  background: 'rgba(239, 68, 68, 0.2)',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease',
                                width: '28px',
                                height: '28px'
                              }}
                            >
                              <Trash2 size={14} />
                            </IconButton>
                          </Box>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                              <FormControl fullWidth required size="small">
                                <InputLabel>Account</InputLabel>
                                <Select
                                  value={entry.account_id}
                                  label="Account"
                                  onChange={(e) => handleJournalEntryChange(index, 'account_id', e.target.value)}
                                  disabled={loadingAccounts}
                                  sx={{
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    }
                                  }}
                                >
                                  {chartOfAccounts.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                      {account.code} - {account.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={6} sm={3.5}>
                              <TextField
                                fullWidth
                                label="Debit Amount"
                                type="number"
                                value={entry.debit_amount}
                                onChange={(e) => handleJournalEntryChange(index, 'debit_amount', e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    },
                                    '&.Mui-focused': {
                                      boxShadow: '0 2px 6px rgba(34, 197, 94, 0.2)'
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={6} sm={3.5}>
                              <TextField
                                fullWidth
                                label="Credit Amount"
                                type="number"
                                value={entry.credit_amount}
                                onChange={(e) => handleJournalEntryChange(index, 'credit_amount', e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    },
                                    '&.Mui-focused': {
                                      boxShadow: '0 2px 6px rgba(34, 197, 94, 0.2)'
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Entry Description"
                                value={entry.description}
                                onChange={(e) => handleJournalEntryChange(index, 'description', e.target.value)}
                                required
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    }
                                  }
                                }}
                                placeholder="Describe this journal entry..."
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                  
                  {/* Enhanced Totals Display */}
                  <Paper sx={{ 
                    p: 2, 
                    mt: 2,
                    borderRadius: 1.5,
                    background: calculateTotals().isBalanced 
                      ? 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)'
                      : 'linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%)',
                    border: calculateTotals().isBalanced 
                      ? '1px solid #22c55e'
                      : '1px solid #ef4444',
                    boxShadow: calculateTotals().isBalanced
                      ? '0 2px 8px rgba(34, 197, 94, 0.15)'
                      : '0 2px 8px rgba(239, 68, 68, 0.15)'
                  }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                            Total Debits
                          </Typography>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: '700',
                            color: '#22c55e',
                            fontSize: '1rem'
                          }}>
                            ${calculateTotals().totalDebits.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                            Total Credits
                          </Typography>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: '700',
                            color: '#ef4444',
                            fontSize: '1rem'
                          }}>
                            ${calculateTotals().totalCredits.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Chip
                            label={calculateTotals().isBalanced ? '✅ Balanced' : '⚠️ Not Balanced'}
                            color={calculateTotals().isBalanced ? 'success' : 'error'}
                            size="small"
                            sx={{
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              height: '28px',
                              borderRadius: 1.5,
                              boxShadow: calculateTotals().isBalanced
                                ? '0 1px 4px rgba(34, 197, 94, 0.2)'
                                : '0 1px 4px rgba(239, 68, 68, 0.2)'
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          borderTop: '1px solid #e2e8f0'
        }}>
          <Button 
            onClick={handleCloseNewTransactionDialog}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: '600',
              px: 2.5,
              py: 1,
              color: '#6b7280',
              '&:hover': {
                background: 'rgba(107, 114, 128, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitNewTransaction}
            disabled={submittingTransaction || !calculateTotals().isBalanced}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: '600',
              px: 3,
              py: 1,
              background: calculateTotals().isBalanced
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
              boxShadow: calculateTotals().isBalanced
                ? '0 2px 8px rgba(34, 197, 94, 0.3)'
                : '0 1px 3px rgba(0,0,0,0.1)',
              '&:hover': {
                background: calculateTotals().isBalanced
                  ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                  : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                boxShadow: calculateTotals().isBalanced
                  ? '0 4px 12px rgba(34, 197, 94, 0.4)'
                  : '0 1px 3px rgba(0,0,0,0.1)',
                transform: calculateTotals().isBalanced ? 'translateY(-1px)' : 'none'
              },
              transition: 'all 0.2s ease',
              '&:disabled': {
                transform: 'none'
              }
            }}
          >
            {submittingTransaction ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Creating...
              </Box>
            ) : (
              '💾 Create Transaction'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog 
        open={showEditTransactionDialog} 
        onClose={handleCloseEditTransactionDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
            overflow: 'visible',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 2,
          position: 'relative'
        }}>
          <Typography variant="h6" component="div" sx={{ 
            fontWeight: '600',
            textAlign: 'center',
            letterSpacing: '0.3px'
          }}>
            Edit Transaction
          </Typography>
          <Box sx={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Edit size={16} color="white" />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }}>
            <Grid container spacing={3}>
              {/* Basic Transaction Information */}
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2.5, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                }}>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: '600', 
                    mb: 2,
                    color: '#2d3748',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1rem'
                  }}>
                    <FileText size={18} />
                    Transaction Details
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transaction Date"
                        type="date"
                        value={editTransactionForm.transaction_date}
                        onChange={(e) => handleEditTransactionFormChange('transaction_date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Transaction Type</InputLabel>
                        <Select
                          value={editTransactionForm.transaction_type}
                          label="Transaction Type"
                          onChange={(e) => handleEditTransactionFormChange('transaction_type', e.target.value)}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <MenuItem value="BOOKING_REVENUE">📊 Booking Revenue</MenuItem>
                          <MenuItem value="EXPENSE">💰 Expense</MenuItem>
                          <MenuItem value="PAYMENT_RECEIVED">💳 Payment Received</MenuItem>
                          <MenuItem value="PAYMENT_MADE">💸 Payment Made</MenuItem>
                          <MenuItem value="ADJUSTMENT">⚖️ Journal Adjustment</MenuItem>
                          <MenuItem value="DEPRECIATION">📉 Depreciation</MenuItem>
                          <MenuItem value="ACCRUAL">📈 Accrual</MenuItem>
                          <MenuItem value="REVERSAL">🔄 Reversal</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={editTransactionForm.description}
                        onChange={(e) => handleEditTransactionFormChange('description', e.target.value)}
                        multiline
                        rows={2}
                        required
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }
                        }}
                        placeholder="Enter transaction description..."
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Property</InputLabel>
                        <Select
                          value={editTransactionForm.property_id}
                          label="Property"
                          onChange={(e) => handleEditTransactionFormChange('property_id', e.target.value)}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          {properties.map((property) => (
                            <MenuItem key={property.id} value={property.id}>
                              🏢 {property.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Currency</InputLabel>
                        <Select
                          value={editTransactionForm.currency_id}
                          label="Currency"
                          onChange={(e) => handleEditTransactionFormChange('currency_id', e.target.value)}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          {currencies.map((currency) => (
                            <MenuItem key={currency.id} value={currency.id}>
                              {currency.symbol} {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit (Optional)</InputLabel>
                        <Select
                          value={editTransactionForm.unit_id}
                          label="Unit (Optional)"
                          onChange={(e) => handleEditTransactionFormChange('unit_id', e.target.value)}
                          disabled={loadingUnits}
                          sx={{
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <MenuItem value="">🏠 None</MenuItem>
                          {units.map((unit) => (
                            <MenuItem key={unit.id} value={unit.id}>
                              🏠 {unit.unit_number} - {unit.name || 'Unit'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Journal Entries */}
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2.5, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: '600',
                      color: '#2d3748',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}>
                      📋 Journal Entries
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Plus size={16} />}
                      onClick={addEditJournalEntry}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6b4190 100%)',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Add Entry
                    </Button>
                  </Box>
                  
                  <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                    {editTransactionForm.journal_entries.map((entry, index) => (
                      <Card key={index} sx={{ 
                        mb: 2, 
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          transform: 'translateY(-1px)'
                        }
                      }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#4a5568' }}>
                              Entry #{index + 1}
                            </Typography>
                            <IconButton
                              color="error"
                              onClick={() => removeEditJournalEntry(index)}
                              disabled={editTransactionForm.journal_entries.length <= 2}
                              size="small"
                              sx={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                '&:hover': {
                                  background: 'rgba(239, 68, 68, 0.2)',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease',
                                width: '28px',
                                height: '28px'
                              }}
                            >
                              <Trash2 size={14} />
                            </IconButton>
                          </Box>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                              <FormControl fullWidth required size="small">
                                <InputLabel>Account</InputLabel>
                                <Select
                                  value={entry.account_id}
                                  label="Account"
                                  onChange={(e) => handleEditJournalEntryChange(index, 'account_id', e.target.value)}
                                  disabled={loadingAccounts}
                                  sx={{
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    }
                                  }}
                                >
                                  {chartOfAccounts.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                      {account.code} - {account.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={6} sm={3.5}>
                              <TextField
                                fullWidth
                                label="Debit Amount"
                                type="number"
                                value={entry.debit_amount}
                                onChange={(e) => handleEditJournalEntryChange(index, 'debit_amount', e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    },
                                    '&.Mui-focused': {
                                      boxShadow: '0 2px 6px rgba(34, 197, 94, 0.2)'
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={6} sm={3.5}>
                              <TextField
                                fullWidth
                                label="Credit Amount"
                                type="number"
                                value={entry.credit_amount}
                                onChange={(e) => handleEditJournalEntryChange(index, 'credit_amount', e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    },
                                    '&.Mui-focused': {
                                      boxShadow: '0 2px 6px rgba(34, 197, 94, 0.2)'
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Entry Description"
                                value={entry.description}
                                onChange={(e) => handleEditJournalEntryChange(index, 'description', e.target.value)}
                                required
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    }
                                  }
                                }}
                                placeholder="Describe this journal entry..."
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                  
                  {/* Enhanced Totals Display */}
                  <Paper sx={{ 
                    p: 2, 
                    mt: 2,
                    borderRadius: 1.5,
                    background: calculateEditTotals().isBalanced 
                      ? 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)'
                      : 'linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%)',
                    border: calculateEditTotals().isBalanced 
                      ? '1px solid #22c55e'
                      : '1px solid #ef4444',
                    boxShadow: calculateEditTotals().isBalanced
                      ? '0 2px 8px rgba(34, 197, 94, 0.15)'
                      : '0 2px 8px rgba(239, 68, 68, 0.15)'
                  }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                            Total Debits
                          </Typography>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: '700',
                            color: '#22c55e',
                            fontSize: '1rem'
                          }}>
                            ${calculateEditTotals().totalDebits.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                            Total Credits
                          </Typography>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: '700',
                            color: '#ef4444',
                            fontSize: '1rem'
                          }}>
                            ${calculateEditTotals().totalCredits.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Chip
                            label={calculateEditTotals().isBalanced ? '✅ Balanced' : '⚠️ Not Balanced'}
                            color={calculateEditTotals().isBalanced ? 'success' : 'error'}
                            size="small"
                            sx={{
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              height: '28px',
                              borderRadius: 1.5,
                              boxShadow: calculateEditTotals().isBalanced
                                ? '0 1px 4px rgba(34, 197, 94, 0.2)'
                                : '0 1px 4px rgba(239, 68, 68, 0.2)'
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          borderTop: '1px solid #e2e8f0'
        }}>
          <Button 
            onClick={handleCloseEditTransactionDialog}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: '600',
              px: 2.5,
              py: 1,
              color: '#6b7280',
              '&:hover': {
                background: 'rgba(107, 114, 128, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitEditTransaction}
            disabled={updatingTransaction || !calculateEditTotals().isBalanced}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: '600',
              px: 3,
              py: 1,
              background: calculateEditTotals().isBalanced
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
              boxShadow: calculateEditTotals().isBalanced
                ? '0 2px 8px rgba(34, 197, 94, 0.3)'
                : '0 1px 3px rgba(0,0,0,0.1)',
              '&:hover': {
                background: calculateEditTotals().isBalanced
                  ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                  : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                boxShadow: calculateEditTotals().isBalanced
                  ? '0 4px 12px rgba(34, 197, 94, 0.4)'
                  : '0 1px 3px rgba(0,0,0,0.1)',
                transform: calculateEditTotals().isBalanced ? 'translateY(-1px)' : 'none'
              },
              transition: 'all 0.2s ease',
              '&:disabled': {
                transform: 'none'
              }
            }}
          >
            {updatingTransaction ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Updating...
              </Box>
            ) : (
              '💾 Update Transaction'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default AccountingTransactions;